import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { OrgContext } from '../../../objects/Context';
import './SettingsComponents.css';

const normalizeHexColor = (value) => {
    if (value === null || value === undefined) return '';
    const trimmed = String(value).trim();
    if (!trimmed) return '';

    const withoutHashes = trimmed.replace(/^#+/, '');
    return withoutHashes ? `#${withoutHashes}` : '';
};

const isCompleteHexColor = (value) => /^#[0-9A-Fa-f]{6}$/.test(value);

function ColorInput({ label, description, value, onChange }) {
    return (
        <div className="text-item-label">
            <div className="title">{label}</div>
            <div className="description">{description}</div>
            <div className="portal-color-row">
                <input
                    type="color"
                    className="portal-color-swatch"
                    value={isCompleteHexColor(value) ? value : '#000000'}
                    onChange={(e) => onChange(e.target.value)}
                />
                <input
                    className="text-item-value portal-color-text"
                    type="text"
                    value={value}
                    onChange={(e) => onChange(normalizeHexColor(e.target.value))}
                    placeholder="#000000"
                    maxLength={7}
                />
                {value && (
                    <button className="portal-color-clear" onClick={() => onChange('')} title="Clear">
                        ✕
                    </button>
                )}
            </div>
        </div>
    );
}

function OrgBrandingSettings() {
    const { org, setOrg } = useContext(OrgContext);

    const [primaryColor, setPrimaryColor] = useState(normalizeHexColor(org.clientPortal?.primaryColor));
    const [secondaryColor, setSecondaryColor] = useState(normalizeHexColor(org.clientPortal?.secondaryColor));
    const [accentColor, setAccentColor] = useState(normalizeHexColor(org.clientPortal?.accentColor));
    const [logoUrl, setLogoUrl] = useState(org.clientPortal?.logoUrl || null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [isPortalChanged, setIsPortalChanged] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error'
    const logoInputRef = useRef(null);

    useEffect(() => {
        const portal = org.clientPortal || {};
        const hasColorChange =
            primaryColor !== normalizeHexColor(portal.primaryColor) ||
            secondaryColor !== normalizeHexColor(portal.secondaryColor) ||
            accentColor !== normalizeHexColor(portal.accentColor);
        setIsPortalChanged(hasColorChange || logoFile !== null);
    }, [primaryColor, secondaryColor, accentColor, logoFile, org.clientPortal]);

    useEffect(() => {
        const portal = org.clientPortal || {};
        setPrimaryColor(normalizeHexColor(portal.primaryColor));
        setSecondaryColor(normalizeHexColor(portal.secondaryColor));
        setAccentColor(normalizeHexColor(portal.accentColor));
        setLogoUrl(portal.logoUrl || null);
    }, [org.clientPortal]);

    // Fetch presigned URL on mount if a logo key is stored
    useEffect(() => {
        if (org.clientPortal?.logoUrl && !logoFile) {
            axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/logo-url/${org.id}`, { withCredentials: true })
                .then(res => setLogoPreview(res.data.presignedUrl))
                .catch(() => setLogoPreview(null));
        }
    }, [org.clientPortal?.logoUrl, org.id]);

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };

    const handleRemoveLogo = () => {
        setLogoFile(null);
        setLogoPreview(null);
        setLogoUrl(null);
        if (logoInputRef.current) logoInputRef.current.value = '';
        // Persist the removal
        axios.put(
            `${process.env.REACT_APP_API_BASE_URL}/api/orgs/update-client-portal/${org.id}`,
            { logoUrl: null },
            { withCredentials: true }
        ).then(res => setOrg(prevOrg => ({ ...prevOrg, clientPortal: res.data.clientPortal })))
         .catch(err => console.error('Error removing logo:', err));
    };

    const handleSave = async () => {
        if (!isPortalChanged) return;
        setIsSaving(true);
        setSaveStatus(null);
        try {
            const normalizedPrimaryColor = normalizeHexColor(primaryColor);
            const normalizedSecondaryColor = normalizeHexColor(secondaryColor);
            const normalizedAccentColor = normalizeHexColor(accentColor);

            setPrimaryColor(normalizedPrimaryColor);
            setSecondaryColor(normalizedSecondaryColor);
            setAccentColor(normalizedAccentColor);

            let newLogoUrl = logoUrl;

            if (logoFile) {
                const formData = new FormData();
                formData.append('logo', logoFile);
                const uploadRes = await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL}/api/orgs/upload-logo/${org.id}`,
                    formData,
                    { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                );
                newLogoUrl = uploadRes.data.clientPortal?.logoUrl;
                setLogoUrl(newLogoUrl);
                setLogoPreview(uploadRes.data.presignedUrl);
                setLogoFile(null);
            }

            const res = await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/orgs/update-client-portal/${org.id}`,
                {
                    primaryColor: normalizedPrimaryColor || null,
                    secondaryColor: normalizedSecondaryColor || null,
                    accentColor: normalizedAccentColor || null,
                },
                { withCredentials: true }
            );

            setOrg(prevOrg => ({ ...prevOrg, clientPortal: res.data.clientPortal }));
            setSaveStatus('success');
            setTimeout(() => setSaveStatus(null), 3000);
        } catch (err) {
            console.error('Error saving client portal settings:', err);
            setSaveStatus('error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="settings-component">
            <div className="settings-header">
                <div className="settings-title-group">
                    <h1 className="settings-title">Branding</h1>
                    <h2 className="settings-subtitle">Customize the logo and colors for your client-facing portal</h2>
                </div>
            </div>
            <div className="content">
                <div className="item-list">
                    <div className="text-item-label">
                        <div className="title">Brand Logo</div>
                        <div className="description">Upload your organization logo (PNG, JPG, SVG — max 5MB)</div>
                        <div className="portal-logo-area">
                            {logoPreview ? (
                                <div className="portal-logo-preview-wrap">
                                    <img src={logoPreview} alt="Logo preview" className="portal-logo-preview" />
                                    <button className="portal-logo-remove" onClick={handleRemoveLogo}>Remove</button>
                                </div>
                            ) : (
                                <button
                                    className="portal-logo-upload-btn"
                                    onClick={() => logoInputRef.current?.click()}
                                >
                                    <span className="portal-logo-upload-icon">↑</span>
                                    Upload Logo
                                </button>
                            )}
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleLogoChange}
                            />
                            {logoPreview && (
                                <button
                                    className="portal-logo-replace-btn"
                                    onClick={() => logoInputRef.current?.click()}
                                >
                                    Replace
                                </button>
                            )}
                        </div>
                    </div>

                    <ColorInput
                        label="Primary Color"
                        description="Main brand color used for headers and primary actions"
                        value={primaryColor}
                        onChange={setPrimaryColor}
                    />
                    <ColorInput
                        label="Secondary Color"
                        description="Supporting color used for backgrounds and secondary elements"
                        value={secondaryColor}
                        onChange={setSecondaryColor}
                    />
                    <ColorInput
                        label="Accent Color"
                        description="Highlight color used for calls-to-action and emphasis"
                        value={accentColor}
                        onChange={setAccentColor}
                    />
                </div>
                <div className="button-container">
                    {saveStatus === 'success' && (
                        <span className="portal-save-msg portal-save-success">Saved successfully</span>
                    )}
                    {saveStatus === 'error' && (
                        <span className="portal-save-msg portal-save-error">Failed to save — please try again</span>
                    )}
                    <button
                        className="save-button"
                        disabled={!isPortalChanged || isSaving}
                        onClick={handleSave}
                    >
                        {isSaving ? 'Saving...' : 'Save Branding'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default OrgBrandingSettings;
